import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { clearUserAttributesCache } from "../../../app/auth/authSessionAttributes";
import { useAuth } from "../../../app/providers/AuthProvider";
import { notify } from "../../../shared/lib/feedback/notify";
import {
  confirmLoginChallenge,
  resolveLoginUsername,
  signInWithPassword,
  startPasswordReset,
} from "../api/login";
import { METHOD_LOGIN_KEY, authRoutes } from "../config/authRoutes";
import {
  getLoginPasswordHelperText,
  getLoginUsernameHelperText,
  isInvalidCredentialsMessage,
  normalizeLoginError,
  normalizeLoginStateMessage,
  normalizePasswordChangeError,
} from "../utils/authErrorMessages";
import { resolvePostAuthRoute } from "../utils/resolvePostAuthRoute";
import { useResendCountdown } from "./useResendCountdown";

const loginSteps = {
  credentials: "credentials",
  mfa: "mfa",
  newPassword: "newPassword",
};

const mfaSignInSteps = {
  CONFIRM_SIGN_IN_WITH_EMAIL_CODE: {
    title: "Digite o código enviado por e-mail",
    description: "Use o código para confirmar sua identidade e continuar.",
    emptyMessage: "Informe o código enviado por e-mail.",
    canResend: true,
    resendSuccessMessage: "Enviamos um novo código por e-mail.",
  },
  CONFIRM_SIGN_IN_WITH_SMS_CODE: {
    title: "Digite o código enviado por SMS",
    description: "Use o código para confirmar sua identidade e continuar.",
    emptyMessage: "Informe o código enviado por SMS.",
    canResend: true,
    resendSuccessMessage: "Enviamos um novo código por SMS.",
  },
  CONFIRM_SIGN_IN_WITH_TOTP_CODE: {
    title: "Digite o código do autenticador",
    description: "Use o código do seu aplicativo autenticador para continuar.",
    emptyMessage: "Informe o código do aplicativo autenticador.",
  },
};

function getMfaCopy(signInStep) {
  return mfaSignInSteps[signInStep] || {
    title: "Digite o código de autenticação",
    description: "Use o código para confirmar sua identidade e continuar.",
    emptyMessage: "Informe o código de autenticação.",
  };
}

export function useLoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, refreshUser, status, user } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [step, setStep] = useState(loginSteps.credentials);
  const [challengeUser, setChallengeUser] = useState(null);
  const [mfaCopy, setMfaCopy] = useState(getMfaCopy());
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const {
    isRunning: resendMfaDisabled,
    restart: restartMfaResendCountdown,
    timeLeft: resendMfaTimeLeft,
  } = useResendCountdown(0);

  useEffect(() => {
    const stateMessage = normalizeLoginStateMessage(location.state?.message);

    if (stateMessage) {
      notify("Success", stateMessage, true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state?.message, navigate]);

  useEffect(() => {
    if (status === "booting") {
      return undefined;
    }

    if (!isAuthenticated) {
      setCheckingSession(false);
      return undefined;
    }

    navigate(resolvePostAuthRoute(user), { replace: true });
    return undefined;
  }, [isAuthenticated, navigate, status, user]);

  const isMfa = step === loginSteps.mfa;
  const isNewPassword = step === loginSteps.newPassword;
  const hasFormError = Boolean(message);

  const usernameError = hasFormError && (
    !username || isInvalidCredentialsMessage(message)
  );
  const passwordError = hasFormError && (
    !password || isInvalidCredentialsMessage(message)
  );
  const mfaError = hasFormError && isMfa;
  const newPasswordError = hasFormError && isNewPassword;
  const confirmPasswordError = hasFormError && isNewPassword;

  const usernameHelperText = usernameError
    ? getLoginUsernameHelperText(message)
    : undefined;
  const passwordHelperText = passwordError
    ? getLoginPasswordHelperText(message)
    : undefined;
  const mfaHelperText = mfaError ? message : undefined;
  const newPasswordHelperText = newPasswordError ? message : undefined;
  const confirmPasswordHelperText = confirmPasswordError ? message : undefined;

  function moveToMfaChallenge(response) {
    setChallengeUser(response);
    setMfaCopy(getMfaCopy(response?.nextStep?.signInStep));
    setStep(loginSteps.mfa);
    setMessage("");
  }

  function moveToNewPasswordChallenge(response) {
    setChallengeUser(response);
    setStep(loginSteps.newPassword);
    setMessage("");
  }

  async function handleSignInResponse(response) {
    if (response?.isSignedIn) {
      await completeSignIn();
      return true;
    }

    if (mfaSignInSteps[response?.nextStep?.signInStep]) {
      moveToMfaChallenge(response);
      return true;
    }

    if (response?.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      moveToNewPasswordChallenge(response);
      return true;
    }

    return false;
  }

  async function completeSignIn() {
    clearUserAttributesCache();
    const refreshedUser = await refreshUser(false);
    navigate(resolvePostAuthRoute(refreshedUser), { replace: true });
  }

  async function submitCredentials() {
    if (!username || !password) {
      setMessage("Por favor, preencha todos os campos.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const cognitoUsername = await resolveLoginUsername(username);
      const response = await signInWithPassword({
        username: cognitoUsername,
        password,
      });

      sessionStorage.setItem(
        METHOD_LOGIN_KEY,
        username.includes("@") ? "EMAIL" : "USERNAME",
      );

      if (await handleSignInResponse(response)) {
        return;
      }

      if (response.nextStep?.signInStep === "RESET_PASSWORD") {
        await startPasswordReset({ username: cognitoUsername });
        navigate(`${authRoutes.resetPassword}?username=${cognitoUsername}`);
        return;
      }

      sessionStorage.setItem(METHOD_LOGIN_KEY, "");
      setMessage(normalizeLoginError(response.message || response.nextStep?.signInStep));
    } catch (error) {
      sessionStorage.setItem(METHOD_LOGIN_KEY, "");
      setMessage(normalizeLoginError(error.message));
    } finally {
      setLoading(false);
    }
  }

  async function submitMfa() {
    if (!mfaCode) {
      setMessage(mfaCopy.emptyMessage);
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await confirmLoginChallenge({
        challengeResponse: mfaCode,
        user: challengeUser,
      });

      if (await handleSignInResponse(response)) {
        return;
      }

      setMessage(normalizeLoginError(response?.message || response?.nextStep?.signInStep));
    } catch (error) {
      setMessage(normalizeLoginError(error.message));
      setMfaCode("");
    } finally {
      setLoading(false);
    }
  }

  async function resendMfaCode() {
    if (loading || resendMfaDisabled || !mfaCopy.canResend) {
      return;
    }

    if (!username || !password) {
      setStep(loginSteps.credentials);
      setMessage("Informe seus dados de acesso para receber um novo código.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const cognitoUsername = await resolveLoginUsername(username);
      const response = await signInWithPassword({
        username: cognitoUsername,
        password,
      });

      if (await handleSignInResponse(response)) {
        setMfaCode("");
        restartMfaResendCountdown(30);
        notify("Success", mfaCopy.resendSuccessMessage || "Enviamos um novo código.", true);
        return;
      }

      setMessage(normalizeLoginError(response?.message || response?.nextStep?.signInStep));
    } catch (error) {
      setMessage(normalizeLoginError(error.message));
    } finally {
      setLoading(false);
    }
  }

  async function submitNewPassword() {
    if (!newPassword || !confirmPassword) {
      setMessage("Por favor, preencha todos os campos.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("As senhas não conferem.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await confirmLoginChallenge({
        challengeResponse: newPassword,
        user: challengeUser,
      });

      if (!(await handleSignInResponse(response))) {
        setMessage(normalizeLoginError(response?.message || response?.nextStep?.signInStep));
      }
    } catch (error) {
      setMessage(normalizePasswordChangeError(error));
      setNewPassword("");
      setConfirmPassword("");
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (isMfa) {
      await submitMfa();
      return;
    }

    if (isNewPassword) {
      await submitNewPassword();
      return;
    }

    await submitCredentials();
  }

  return {
    checkingSession,
    handleSubmit,
    isMfa,
    isNewPassword,
    loading,
    message,
    mfaCode,
    mfaCopy,
    mfaError,
    mfaHelperText,
    resendMfaCode,
    resendMfaDisabled,
    resendMfaTimeLeft,
    confirmPassword,
    confirmPasswordError,
    confirmPasswordHelperText,
    newPassword,
    newPasswordError,
    newPasswordHelperText,
    password,
    passwordError,
    passwordHelperText,
    setMfaCode,
    setConfirmPassword,
    setNewPassword,
    setPassword,
    setUsername,
    username,
    usernameError,
    usernameHelperText,
  };
}
